import { separateReactNodes } from 'utils/react';
import { keyboardEventCodeAttributes, keyboardEventKeyAttributes, } from '../utils/keyboardUtils';
/**
 * Creates a pretty key combination from a given key combination tuple.
 *
 * @example
 *   const defaultStyle = createPrettyKeyCombination({
 *     binding: [['$mod', 'Alt'], 'KeyC'],
 *   });
 *
 *   // Windows: Ctrl+Alt+C
 *   // macOS: ⌘+⌥+C
 *
 *   const customStyle = createPrettyKeyCombination(
 *     { binding: [['$mod', 'Alt'], 'KeyC'] },
 *     {
 *       keySeparator: ' ',
 *       keyTransformFn: (key, attributes) => (
 *         <KeyboardKey>{key}</KeyboardKey>
 *       ),
 *     },
 *   );
 *
 * @param keyCombination The key combination to prettify.
 * @param options The options.
 */
export function createPrettyKeyCombination(keyCombination, options = {}) {
    const { codeKeyMap = {}, keySeparator = '+', keyTransformFn = (key) => key, preferBinding = false, } = options;
    const { binding, presentation } = keyCombination;
    const isUsingBinding = preferBinding || !presentation;
    const keys = (isUsingBinding ? binding : presentation).flat();
    const keyNodes = keys.filter(Boolean).map((key) => {
        var _a, _b;
        /** Key inferred from the given code to key map. */
        const codeKey = codeKeyMap[key];
        // Attributes from key
        const codeAttributes = keyboardEventCodeAttributes[key];
        const keyAttributes = keyboardEventKeyAttributes[isUsingBinding ? codeKey : key.toLowerCase()];
        const attributes = keyAttributes !== null && keyAttributes !== void 0 ? keyAttributes : codeAttributes;
        const renderedKey = (_b = (_a = codeKey === null || codeKey === void 0 ? void 0 : codeKey.toUpperCase()) !== null && _a !== void 0 ? _a : codeAttributes === null || codeAttributes === void 0 ? void 0 : codeAttributes.label) !== null && _b !== void 0 ? _b : key.replace(/^(Key|Digit)/, '');
        return keyTransformFn(renderedKey, attributes);
    });
    return separateReactNodes(keyNodes, { separator: keySeparator });
}
/**
 * Creates pretty key combinations from a list of given key combination tuples.
 *
 * @example
 *   const customStyle = createPrettyKeyCombinations(
 *     [
 *       { binding: [['$mod', 'Alt'], 'KeyC'] },
 *       { binding: [['$mod', 'Shift', 'Alt'], 'KeyC'] },
 *     ],
 *     {
 *       keySeparator: '',
 *       keyTransformFn: (key, attributes) => (
 *         <Tooltip
 *           dark
 *           content={attributes?.description}
 *           disabled={!attributes?.description}
 *         >
 *           <KeyboardKey>{key}</KeyboardKey>
 *         </Tooltip>
 *       ),
 *       keyCombinationSeparator: ' or ',
 *       keyCombinationTransformFn: (keyCombination) => (
 *         <FlexBox alignItems="center" gap="100">
 *           {keyCombination}
 *         </FlexBox>
 *       ),
 *     },
 *   );
 *
 * @param keyCombinations The key combinations to prettify.
 * @param options The options.
 */
export function createPrettyKeyCombinations(keyCombinations, options = {}) {
    const { codeKeyMap, keySeparator, keyTransformFn, keyCombinationTransformFn: nodeTransformFn = (node) => node, keyCombinationSeparator: separator = ' or ', } = options;
    const transformedKeyCombinations = keyCombinations.map((keyCombination) => createPrettyKeyCombination(keyCombination, {
        codeKeyMap,
        keySeparator,
        keyTransformFn,
    }));
    return separateReactNodes(transformedKeyCombinations, {
        separator,
        nodeTransformFn,
    });
}
